import { MapPin, Calendar, Shield, Clock, Building2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import type { CarePlanData } from '@/hooks/useCarePlan';

interface PlanHeaderProps {
  plan: CarePlanData;
}

export function PlanHeader({ plan }: PlanHeaderProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    computing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    ready: isAhp ? (ahpDark ? 'bg-green-900/30 text-green-400 border-green-700/30' : 'bg-green-100 text-green-800 border-green-300') : 'bg-green-500/20 text-green-400 border-green-500/30',
    error: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className={`text-2xl font-bold ${isAhp ? (ahpDark ? 'text-civic-warm' : 'text-gray-900') : 'text-foreground'}`}>
          {plan.plan_name}
        </h1>
        <Badge className={`text-xs border ${statusColors[plan.status] || statusColors.pending}`}>
          {plan.status === 'ready' ? 'Optimized' : plan.status}
        </Badge>
        {plan.consolidation_level != null && (
          <Badge className={`text-xs border ${
            isAhp
              ? ahpDark ? 'bg-civic-navy-soft/15 text-civic-navy-soft border-civic-navy-soft/30' : 'bg-blue-50 text-blue-700 border-blue-200'
              : 'bg-foreground/5 text-muted-foreground border-foreground/10'
          }`}>
            <Building2 className="w-3 h-3 mr-1" />
            {plan.consolidation_level <= 24 ? 'Max Savings' :
             plan.consolidation_level <= 49 ? 'More Savings' :
             plan.consolidation_level === 50 ? 'Balanced' :
             plan.consolidation_level <= 74 ? 'More Convenient' :
             'Most Convenient'}
          </Badge>
        )}
      </div>

      <div className={`flex flex-wrap gap-4 text-sm ${isAhp ? (ahpDark ? 'text-gray-400' : 'text-gray-600') : 'text-muted-foreground'}`}>
        {plan.location_label && (
          <span className="flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" />
            {plan.location_label}
            {plan.search_radius_miles && ` (${plan.search_radius_miles}mi radius)`}
          </span>
        )}
        {plan.plan_year && (
          <span className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            {plan.plan_year}
          </span>
        )}
        {plan.insurance_status && (
          <span className="flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5" />
            {plan.insurance_status}
          </span>
        )}
        {plan.family_member_name && (
          <span className="flex items-center gap-1.5">
            Patient: {plan.family_member_name}
          </span>
        )}
        <span className="flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          {new Date(plan.created_at).toLocaleDateString()}
        </span>
      </div>
    </div>
  );
}
